import { CircleHelpIcon } from "lucide-react";
import { cn } from "@/lib/cn";
import type { PROD } from "@/lib/data/words";

export type CardFace = "image" | "hints";

/** Flip card: the word's picture on the front, stressed-syllable hints on the back. */
export const WordCard = ({
  word,
  cardFace,
  activeSyllable,
}: {
  word: (typeof PROD)[number];
  cardFace: CardFace;
  activeSyllable: number | null;
}) => {
  const WordIcon = word.image;

  return (
    <div className="perspective-distant flex h-48 min-h-48 w-full max-w-xs flex-none sm:h-56 sm:min-h-56">
      <div
        className={cn(
          "transform-3d relative h-full min-h-0 flex-1 transition-transform duration-500",
          cardFace === "hints" && "transform-[rotateY(180deg)]",
        )}
      >
        <div className="backface-hidden absolute inset-0 overflow-hidden rounded-3xl border border-foreground/30 bg-card p-3">
          <div className="flex h-full min-h-0 items-center justify-center">
            {WordIcon ? (
              <WordIcon className="h-full max-h-28 w-full max-w-28 p-2" />
            ) : (
              <CircleHelpIcon className="h-24 w-24 text-muted-foreground" />
            )}
          </div>
        </div>

        <div className="backface-hidden transform-[rotateY(180deg)] absolute inset-0 overflow-hidden rounded-3xl border border-foreground/30 bg-card p-3">
          <div className="flex h-full items-center justify-center">
            <div
              className="grid w-full items-end gap-x-0 gap-y-2"
              style={{
                gridTemplateColumns: `repeat(${word.displaySyllables.length}, minmax(0, 1fr))`,
                maxWidth: `${word.displaySyllables.length * 2.25}rem`,
              }}
            >
              {word.displaySyllables.map((syllable, syllableIdx) => {
                return (
                  <div
                    // biome-ignore lint/suspicious/noArrayIndexKey: static list, may have duplicates
                    key={syllableIdx}
                    className={cn(
                      "flex min-w-0 flex-col items-center gap-2 rounded-lg py-1 transition-colors duration-100 motion-reduce:transition-none",
                      syllableIdx === activeSyllable && "bg-brand/10",
                    )}
                  >
                    <div
                      className={cn(
                        "w-4/5 rounded-full transition-[background-color,box-shadow] duration-100 motion-reduce:transition-none",
                        syllableIdx === word.stressIndex
                          ? "bg-current"
                          : "bg-current/40",
                        syllableIdx === activeSyllable &&
                          "bg-brand shadow-[0_0_0_4px_rgb(184_117_48_/_0.12)]",
                      )}
                      style={{
                        height: syllableIdx === word.stressIndex ? 80 : 48,
                      }}
                    />
                    <span
                      className={cn(
                        "text-center text-base leading-none transition-colors duration-100 motion-reduce:transition-none",
                        syllableIdx === word.stressIndex && "font-bold",
                        syllableIdx === activeSyllable && "text-brand",
                      )}
                    >
                      {syllable}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
